/*
  Pots de Fleurs & Co : haut de la fiche produit.
  1. Demande de devis (sections/quote-drawer.liquid, snippets/quote-call-buttons.liquid) et
     encadré « Professionnel ? » (snippets/encadre-pro.liquid)
  2. Compteur de la galerie (snippets/product-media-gallery.liquid)
  3. Caractéristiques techniques sous les logos clients, sur mobile
  Repris du thème Verano Design (assets/verano.js), sans les nuanciers. Montants du devis HT pour
  un client pro (theme.prixAffiche, snippets/js-variables.liquid).
*/
(() => {
  const formatMoney = (cents) => theme.Currency.formatMoney(theme.prixAffiche ? theme.prixAffiche(cents) : cents, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
  const fill = (text, values) => String(text || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  const escapeHtml = (text) => String(text ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const lireJson = (texte, repli = null) => {
    if (!texte) return repli;
    try {
      return JSON.parse(texte);
    } catch (error) {
      return repli;
    }
  };


  /* 1. Demande de devis
     ------------------------------------------------------------ */

  // La liste est gardée dans le navigateur du client (localStorage) pour survivre aux changements
  // de page ; sans stockage (navigation privée), elle reste en mémoire le temps de la page.
  const QUOTE_KEY = 'pdf-quote';
  let quoteMemory = [];
  const quoteDrawer = () => document.getElementById('QuoteDrawer');
  const strings = () => lireJson(quoteDrawer()?.querySelector('[data-quote-strings]')?.textContent, {});

  const readQuote = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(QUOTE_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return quoteMemory;
    }
  };

  const writeQuote = (items) => {
    quoteMemory = items;
    try {
      window.localStorage.setItem(QUOTE_KEY, JSON.stringify(items));
    } catch (error) {
      // Stockage indisponible : la liste reste en mémoire.
    }
    renderQuote();
  };

  // Une ligne identique (même variante) voit sa quantité augmenter.
  const addToQuote = (lines) => {
    const items = readQuote();
    lines.forEach((line) => {
      const existing = items.find((item) => item.id === line.id);
      if (existing) existing.quantity += line.quantity;
      else items.push(line);
    });
    writeQuote(items);
  };

  const quoteTotal = (items) => items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);

  // Texte des produits pour l'e-mail : une ligne par produit, puis son lien.
  const quoteSummary = (items) => items.map((item) => {
    const details = [item.variant, fill(strings().quantity, { quantity: item.quantity }), item.price ? formatMoney(item.price * item.quantity) : ''].filter(Boolean).join(' · ');
    return `- ${item.product} : ${details}\n  ${item.url}`;
  }).join('\n');

  const renderQuote = () => {
    const items = readQuote();

    // Compteur de l'icône devis de l'en-tête (snippets/quote-header-icon.liquid).
    document.querySelectorAll('[data-quote-toggle]').forEach((button) => {
      const count = button.querySelector('[data-quote-count]');
      if (count) {
        count.textContent = items.length;
        count.hidden = items.length === 0;
      }
      const label = button.dataset.quoteLabel;
      button.setAttribute('aria-label', items.length ? `${label} (${items.length})` : label);
    });

    const quote = quoteDrawer();
    if (!quote) return;
    const texts = strings();

    quote.querySelector('[data-quote-items]').innerHTML = items.map((item, index) => `
      <li class="quote-item flex items-center gap-3" data-quote-index="${index}">
        <a href="${escapeHtml(item.url)}" class="quote-item__media media media--square overflow-hidden shrink-0">${item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy">` : ''}</a>
        <div class="quote-item__info grid gap-1 leading-tight grow">
          <a href="${escapeHtml(item.url)}" class="font-medium">${escapeHtml(item.product)}</a>
          ${item.variant ? `<span class="text-xs text-opacity">${escapeHtml(item.variant)}</span>` : ''}
          ${item.price ? `<span class="text-sm">${escapeHtml(formatMoney(item.price * item.quantity))}</span>` : ''}
        </div>
        <div class="quote-item__actions grid gap-2 justify-items-end shrink-0">
          <div class="quote-item__quantity flex items-center">
            <button type="button" data-quote-step="-1" aria-label="${escapeHtml(texts.decrease)}">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-quote-step="1" aria-label="${escapeHtml(texts.increase)}">+</button>
          </div>
          <button type="button" class="link text-xs" data-quote-remove>${escapeHtml(texts.remove)}</button>
        </div>
      </li>`).join('');

    quote.querySelector('[data-quote-total-row]').hidden = items.length === 0 || quoteTotal(items) === 0;
    quote.querySelector('[data-quote-total]').textContent = formatMoney(quoteTotal(items)) + (window.pdfModePrix ? ' HT' : '');
    syncQuotePro(quote, items);
  };

  // La case « Je suis professionnel » affiche société, SIRET et métier (obligatoires une fois
  // cochée). Sans produit dans la liste, le panneau devient une demande de compte pro.
  const syncQuotePro = (quote, items = readQuote()) => {
    const pro = quote.querySelector('[data-quote-pro]');
    const actif = !!pro?.checked;
    const compteSeul = actif && items.length === 0;

    quote.querySelector('[data-quote-pro-champs]').hidden = !actif;
    quote.querySelectorAll('[data-quote-pro-requis]').forEach((champ) => {
      champ.disabled = !actif;
      champ.required = actif;
    });

    const titre = quote.querySelector('[data-quote-title]');
    titre.textContent = compteSeul ? titre.dataset.titrePro : titre.dataset.titreDevis;
    quote.querySelector('[data-quote-pro-intro]').hidden = !compteSeul;
    const intro = quote.querySelector('[data-quote-intro]');
    if (intro) intro.hidden = items.length > 0 || compteSeul;
    quote.querySelector('[data-quote-empty]').hidden = items.length > 0 || compteSeul;
    // Le produit consulté n'accompagne qu'une demande professionnelle.
    const produitConsulte = quote.querySelector('[data-quote-produit-consulte]');
    produitConsulte.disabled = !actif || !produitConsulte.value;

    const demande = quote.querySelector('[data-quote-demande]');
    demande.value = compteSeul ? demande.dataset.pro : demande.dataset.devis;
  };

  const showQuoteState = (quote, state) => {
    quote.querySelector('#QuoteForm').hidden = state === 'success';
    quote.querySelector('[data-quote-success]').hidden = state !== 'success';
    quote.querySelector('[data-quote-error]').hidden = state !== 'error';
  };

  // La protection anti-spam de Shopify ne s'attache au formulaire qu'au premier focus dans un
  // champ : on la prévient à l'ouverture, sinon un client aux champs déjà remplis ne peut envoyer.
  const preparerCaptcha = (quote) => {
    const form = quote.querySelector('#QuoteForm');
    if (!form || form.dataset.hcaptchaBound || form.dataset.recaptchaBound) return;
    form.querySelector('input:not([type="hidden"]), textarea')?.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
  };

  // options.pro : ouvre la section professionnelle ; options.produit : produit consulté, joint à la
  // demande (encadré « Professionnel ? »).
  const openQuote = (activeElement = null, options = {}) => {
    const quote = quoteDrawer();
    if (!quote) return;
    preparerCaptcha(quote);

    if (options.pro) quote.querySelector('[data-quote-pro]').checked = true;
    quote.querySelector('[data-quote-produit-consulte]').value = options.produit || '';

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.open) cartDrawer.hide();

    renderQuote();
    showQuoteState(quote, 'form');
    quote.show(activeElement);
  };

  const quantityOf = (formId) => {
    const input = document.querySelector(`input[name="quantity"][form="${formId}"]`);
    return Math.max(parseInt(input?.value, 10) || 1, 1);
  };

  // Ligne de devis pour le produit affiché : variante choisie, quantité, image, prix affiché.
  const productLine = (button) => {
    const container = button.closest('product-info, .quick-view') || document;
    const variant = lireJson(container.querySelector('[data-selected-variant]')?.textContent);
    if (!variant) return null;
    const image = variant.featured_media?.preview_image?.src;

    return {
      id: variant.id,
      product: button.dataset.productTitle,
      variant: variant.title !== 'Default Title' ? variant.title : '',
      quantity: quantityOf(button.dataset.form),
      image: image ? `${image}${image.includes('?') ? '&' : '?'}width=180` : button.dataset.productImage,
      url: `${button.dataset.productUrl}?variant=${variant.id}`,
      // Produit sur devis pour un pro suisse (snippets/lyxo-devis-bouton.liquid) : ligne sans prix.
      price: button.hasAttribute('data-sans-prix') ? 0 : variant.price
    };
  };

  // « Vu récemment » du panier latéral : affiché panier vide seulement. Le Liquid règle le
  // chargement, ceci suit les ajouts et retraits faits sans recharger la page ; si l'onglet
  // masqué était ouvert, le panneau revient sur l'onglet Panier.
  window.theme?.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.cart?.errors || typeof event?.cart?.item_count !== 'number') return;
    const masquer = event.cart.item_count > 0;
    document.querySelectorAll('[data-vu-recemment-panier]').forEach((onglet) => {
      onglet.hidden = masquer;
      if (masquer && onglet.querySelector('[aria-selected="true"]')) onglet.closest('[is="tab-list"]')?.reset();
    });
  });

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest?.('[data-quote-add]');
    if (addButton) {
      const line = productLine(addButton);
      if (line) addToQuote([line]);
      openQuote(addButton);
      return;
    }

    // Bouton « Demander un devis pour mon panier » du panier latéral (snippets/quote-from-cart.liquid).
    const cartButton = event.target.closest?.('[data-quote-from-cart]');
    if (cartButton) {
      const lines = lireJson(cartButton.parentElement.querySelector('[data-quote-cart]')?.textContent, []);
      if (lines.length) addToQuote(lines);
      openQuote(cartButton);
      return;
    }

    const toggle = event.target.closest?.('[data-quote-toggle]');
    if (toggle) {
      openQuote(toggle);
      return;
    }

    const demandePro = event.target.closest?.('[data-quote-pro-open]');
    if (demandePro) {
      openQuote(demandePro, { pro: true, produit: `${demandePro.dataset.produit} (${demandePro.dataset.produitUrl})` });
      return;
    }

    const encadre = event.target.closest?.('[data-encadre-pro]');
    if (encadre && event.target.closest('[data-encadre-pro-fermer], [data-encadre-pro-ouvrir]')) {
      const fermer = !!event.target.closest('[data-encadre-pro-fermer]');
      encadre.querySelector('[data-encadre-pro-boite]').hidden = fermer;
      encadre.querySelector('[data-encadre-pro-ouvrir]').hidden = !fermer;
      try {
        if (fermer) window.localStorage.setItem('pdf-encadre-pro', 'ferme');
        else window.localStorage.removeItem('pdf-encadre-pro');
      } catch (error) {
        // Stockage indisponible : le choix vaut pour la page seulement.
      }
      return;
    }

    // Quantité et retrait dans le panneau.
    const row = event.target.closest?.('[data-quote-index]');
    if (!row) return;
    const items = readQuote();
    const item = items[parseInt(row.dataset.quoteIndex, 10)];
    if (!item) return;

    const step = event.target.closest('[data-quote-step]');
    if (step) {
      item.quantity = Math.max(item.quantity + parseInt(step.dataset.quoteStep, 10), 1);
      writeQuote(items);
    }
    else if (event.target.closest('[data-quote-remove]')) {
      writeQuote(items.filter((entry) => entry !== item));
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-quote-pro]')) return;
    const quote = event.target.closest('#QuoteDrawer');
    if (quote) syncQuotePro(quote);
  });

  // Envoi par le formulaire de contact de Shopify (e-mail à la boutique) : les produits sont
  // copiés dans le formulaire, et le retour se fait sur la page avec ?devis=envoye.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.id !== 'QuoteForm') return;

    const items = readQuote();
    form.querySelector('[data-quote-field="items"]').value = quoteSummary(items);
    form.querySelector('[data-quote-field="total"]').value = quoteTotal(items) ? formatMoney(quoteTotal(items)) + (window.pdfModePrix ? ' HT' : '') : '';
    form.querySelector('[data-quote-return]').value = `${window.location.pathname}?devis=envoye`;

    // Un seul envoi à la fois : un second clic déclencherait la protection anti-spam.
    const submit = form.querySelector('[data-quote-submit]');
    if (submit.hasAttribute('data-envoi')) {
      event.preventDefault();
      return;
    }
    submit.setAttribute('data-envoi', '');
    submit.setAttribute('aria-busy', 'true');
    const texte = submit.querySelector('.btn-text');
    if (texte) {
      texte.dataset.texte = texte.dataset.texte || texte.textContent;
      if (strings().sending) texte.textContent = strings().sending;
    }
  });

  // Retour arrière vers une page gardée en cache après un envoi : le bouton redevient cliquable.
  window.addEventListener('pageshow', () => {
    const submit = document.querySelector('#QuoteForm [data-quote-submit]');
    if (!submit?.hasAttribute('data-envoi')) return;
    submit.removeAttribute('data-envoi');
    submit.removeAttribute('aria-busy');
    const texte = submit.querySelector('.btn-text');
    if (texte) texte.textContent = texte.dataset.texte || texte.textContent;
  });

  // Retour du formulaire de contact : la liste est vidée et le panneau se rouvre sur le message
  // de réussite ; en cas d'erreur, il se rouvre sur le formulaire.
  const quoteReturn = () => {
    const quote = quoteDrawer();
    if (!quote) return;
    const params = new URLSearchParams(window.location.search);
    const sent = params.get('devis') === 'envoye';
    const failed = window.location.hash === '#QuoteForm';
    if (!sent && !failed) return;

    params.delete('devis');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

    if (sent) {
      writeQuote([]);
      showQuoteState(quote, 'success');
      setTimeout(() => quote.show(), 500);
    }
    else {
      setTimeout(() => openQuote(), 500);
    }
  };

  window.addEventListener('storage', (event) => {
    if (event.key === QUOTE_KEY) renderQuote();
  });

  renderQuote();
  quoteReturn();


  /* 2. Compteur de la galerie
     ------------------------------------------------------------ */

  // « 2 / 9 » sur la photo : il suit la vignette active, au défilement comme au toucher.
  const bindGalleryCounters = () => document.querySelectorAll('[data-gallery-counter]').forEach((counter) => {
    if (counter.dataset.galleryBound) return;
    const list = counter.closest('.product__gallery')?.querySelector('.product__thumbnails-list');
    if (!list) return;

    const refresh = () => {
      const thumbnails = [...list.querySelectorAll('.product__thumbnail')];
      const current = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
      if (current < 0) return;
      counter.textContent = `${current + 1} / ${thumbnails.length || counter.dataset.galleryTotal}`;
    };

    refresh();
    new MutationObserver(refresh).observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
    counter.dataset.galleryBound = 'true';
  });

  bindGalleryCounters();
  document.addEventListener('quickview:loaded', bindGalleryCounters);


  /* 3. Caractéristiques techniques sous les logos clients, sur mobile
     ------------------------------------------------------------ */

  // Réglage « Caractéristiques techniques sous les logos clients sur mobile » de la section
  // produit : la section des caractéristiques est déplacée juste sous le bloc des logos, et
  // remise à sa place en passant sur ordinateur.
  const info = document.querySelector('product-info[data-specs-below-logos]');
  const logos = info?.querySelector('.client-logos:not(.client-logos--below-media)');
  const specs = document.querySelector('.product-specifications-section')?.closest('.shopify-section');
  if (info && logos && specs) {
    const place = document.createComment('caracteristiques');
    specs.before(place);
    const mobile = window.matchMedia('(max-width: 1023px)');
    const placer = () => {
      if (mobile.matches) logos.after(specs);
      else place.after(specs);
      specs.classList.toggle('specs-sous-logos', mobile.matches);
    };
    placer();
    mobile.addEventListener('change', placer);
  }
})();
